import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useRevokePersonalAccessToken } from '../hooks'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface Props {
  tokenId: string | null
  tokenName: string | null
  onClose: () => void
}

export function RevokeTokenDialog({ tokenId, tokenName, onClose }: Props) {
  const { t } = useTranslation('settings')
  const revoke = useRevokePersonalAccessToken()

  const open = tokenId !== null

  const handleConfirm = async () => {
    if (!tokenId) return
    try {
      await revoke.mutateAsync(tokenId)
      onClose()
    } catch {
      toast.error(t('integrationsPage.revokeConfirm.revokeFailed'))
    }
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !revoke.isPending) onClose()
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('integrationsPage.revokeConfirm.title')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {tokenName ? `"${tokenName}" — ` : ''}
            {t('integrationsPage.revokeConfirm.body')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={revoke.isPending}>
            {t('integrationsPage.revokeConfirm.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault()
              handleConfirm()
            }}
            disabled={revoke.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {revoke.isPending && <Loader2 className="size-4 animate-spin mr-2" />}
            {t('integrationsPage.revokeConfirm.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
